import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Globe, 
  TrendingUp,
  BarChart3,
  Target,
  Zap,
  Shield,
  Award,
  Phone,
  Calendar,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Users,
  Database,
  Bot,
  DollarSign,
  LineChart,
  Settings
} from 'lucide-react';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const Home = () => {
  const whatsappNumber = "065 999 4443";
  const whatsappLink = "https://wa.me/27659994443?text=Hi%20I%20need%20assistance";
  const calendlyLink = "https://calendly.com"; // Replace with actual Calendly link

  const servicePillars = [
    {
      icon: <Globe className="w-16 h-16 text-gold" />,
      title: "Digital Revenue Foundations",
      description: "Strategic digital infrastructure that drives measurable revenue growth",
      services: [
        "Premium business websites",
        "Lead generation funnels",
        "Conversion architecture",
        "CRM & automation setup",
        "Business process automation",
        "WhatsApp integration systems"
      ],
      image: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NzIzMjY1MDV8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <TrendingUp className="w-16 h-16 text-gold" />,
      title: "Margin & Pricing Intelligence",
      description: "Commercial governance systems that protect and optimize profitability",
      services: [
        "Pricing structure audits",
        "Discount governance frameworks",
        "Margin guardrail design",
        "Scenario modelling & elasticity analysis",
        "Executive profitability dashboards",
        "AI-assisted deal scoring systems"
      ],
      image: "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NzIzMjY1MDV8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Bot className="w-16 h-16 text-gold" />,
      title: "AI & Automation Systems",
      description: "Intelligent systems that scale decision-making and operational efficiency",
      services: [
        "Custom WhatsApp bots",
        "Workflow automation",
        "AI-driven decision support tools",
        "Data dashboards (Python/SQL/Fabric-ready)",
        "Internal commercial control systems"
      ],
      image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NzIzMjY1MTh8MA&ixlib=rb-4.1.0&q=85"
    }
  ];

  const pricingTiers = [
    {
      category: "Website & Revenue Foundation Packages",
      tiers: [
        { name: "Essential Build", price: "from R9,500", description: "Strategic web presence with conversion optimization" },
        { name: "Growth Engine", price: "from R35,000", description: "Full revenue foundation with CRM and automation" },
        { name: "Revenue Architecture Suite", price: "Custom Quote", description: "Complete digital revenue ecosystem" }
      ]
    },
    {
      category: "Margin Intelligence & Commercial Systems",
      tiers: [
        { name: "Diagnostic Sprint", price: "from R75,000", description: "Pricing audit and margin opportunity analysis" },
        { name: "Margin Control Sprint", price: "from R150,000", description: "Full governance framework and dashboard deployment" },
        { name: "Enterprise Governance Build", price: "Custom", description: "End-to-end commercial control architecture" }
      ]
    }
  ];

  const framework = [
    { 
      step: "Diagnose", 
      icon: <Target className="w-12 h-12" />,
      description: "Revenue leakage analysis, margin pressure diagnostics, process audit" 
    },
    { 
      step: "Design", 
      icon: <Lightbulb className="w-12 h-12" />,
      description: "Commercial architecture blueprint, governance frameworks, system specs" 
    },
    { 
      step: "Deploy", 
      icon: <Zap className="w-12 h-12" />,
      description: "System build, automation setup, dashboard configuration, integration" 
    },
    { 
      step: "Govern", 
      icon: <Shield className="w-12 h-12" />,
      description: "Monitoring frameworks, control protocols, continuous optimization" 
    }
  ];

  const caseStudies = [
    {
      title: "Telematics Pricing Portfolio",
      result: "R20M Revenue Uplift",
      description: "Complete pricing restructure with AI-driven elasticity modeling and dynamic discount governance",
      metrics: ["35% margin improvement", "R20M annual uplift", "6-month deployment"]
    },
    {
      title: "AI-Driven Pricing Assistant",
      result: "40% Turnaround Reduction",
      description: "Custom WhatsApp bot with deal scoring, approval workflows, and automated pricing intelligence",
      metrics: ["40% faster quotes", "95% accuracy rate", "Zero revenue leakage"]
    }
  ];

  return (
    <div className="min-h-screen bg-navy">
      <FloatingWhatsApp />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1769771744699-7b73a101b318?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBib2FyZHJvb218ZW58MHx8fHwxNzcyMzI2NDY3fDA&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-navy/97 via-navy/95 to-navy-dark/90"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 py-20 text-center max-w-5xl">
          <div className="h-1 w-24 bg-gold mx-auto mb-8"></div>
          
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-8 leading-tight">
            Profit Isn't Accidental.<br />
            <span className="text-gold">It's Engineered.</span>
          </h1>
          
          <div className="h-1 w-32 bg-gold mx-auto mb-8"></div>
          
          <p className="text-2xl md:text-3xl text-black mb-12 max-w-4xl mx-auto font-light leading-relaxed">
            We design revenue systems, pricing governance, and AI-driven commercial architecture for serious businesses.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-gold hover:bg-gold-light text-navy font-bold text-lg px-12 py-7 rounded-none transition-all duration-300 hover:scale-105 shadow-2xl"
              onClick={() => window.open(calendlyLink, '_blank')}
            >
              <Calendar className="mr-3 w-6 h-6" />
              Book a Strategic Revenue Review
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-gold text-gold hover:bg-gold hover:text-navy font-semibold text-lg px-12 py-7 rounded-none transition-all duration-300"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            >
              Our Capabilities
              <ArrowRight className="ml-3 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="py-20 bg-navy-dark border-y border-gold/20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div className="border-l-2 border-gold pl-6">
                <div className="text-5xl font-bold text-gold mb-2">7+</div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">Years in Pricing & Profitability</div>
              </div>
              <div className="border-l-2 border-gold pl-6">
                <div className="text-5xl font-bold text-gold mb-2">R20M+</div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">Revenue Uplift Delivered</div>
              </div>
              <div className="border-l-2 border-gold pl-6">
                <div className="text-5xl font-bold text-gold mb-2">8-14%</div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">Margin Improvements</div>
              </div>
              <div className="border-l-2 border-gold pl-6">
                <div className="text-5xl font-bold text-gold mb-2">AI+SAP</div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">Enterprise Systems</div>
              </div>
            </div>
            
            <Separator className="my-12 bg-gold/20" />
            
            <div className="text-center">
              <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
                <span className="text-gold font-semibold">Honours Economics + Masters in AI</span> • Enterprise SAP CO-PA transformation exposure • 
                AI-driven commercial modelling specialist
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              Service Architecture
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Three core pillars that transform revenue chaos into commercial control
            </p>
          </div>

          <div className="space-y-16">
            {servicePillars.map((pillar, index) => (
              <Card 
                key={index}
                className="overflow-hidden border-none shadow-2xl"
              >
                <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`relative h-96 overflow-hidden ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <img 
                      src={pillar.image} 
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy/60 to-transparent"></div>
                    <div className="absolute bottom-8 left-8">
                      {pillar.icon}
                    </div>
                  </div>
                  
                  <CardContent className="p-12 flex flex-col justify-center">
                    <h3 className="text-4xl font-bold text-navy mb-4">{pillar.title}</h3>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">{pillar.description}</p>
                    
                    <div className="space-y-4">
                      {pillar.services.map((service, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle2 className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-lg">{service}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-navy-light">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              The Revenue Architecture Framework<sup className="text-2xl">™</sup>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              A systematic approach to building commercial governance that scales
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {framework.map((phase, index) => (
              <div key={index} className="relative">
                <Card className="bg-white/5 backdrop-blur-sm border-gold/30 hover:border-gold transition-all duration-300 h-full">
                  <CardContent className="p-8 text-center">
                    <div className="text-gold mb-6 flex justify-center">
                      {phase.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">{phase.step}</h3>
                    <p className="text-gray-400 leading-relaxed">{phase.description}</p>
                  </CardContent>
                </Card>
                {index < framework.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-gold" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              Proven Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Commercial transformation delivered across pricing, systems, and AI deployment
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {caseStudies.map((study, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-gold transition-all duration-300 hover:shadow-2xl">
                <CardHeader className="bg-navy-dark text-white p-8">
                  <CardTitle className="text-2xl mb-3">{study.title}</CardTitle>
                  <Badge className="bg-gold text-navy text-lg px-4 py-2 font-bold">
                    {study.result}
                  </Badge>
                </CardHeader>
                <CardContent className="p-8">
                  <p className="text-gray-700 mb-6 leading-relaxed">{study.description}</p>
                  <div className="space-y-3">
                    {study.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle2 className="w-5 h-5 text-gold mr-2" />
                        <span className="text-gray-600">{metric}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Thought Leadership Section */}
      <section className="py-24 bg-navy">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-8">
                Why Revenue Architecture Matters
              </h2>
            </div>
            
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Most businesses treat profitability as a trailing indicator—something you measure after the fact. 
                But in high-stakes commercial environments, <span className="text-gold font-semibold">profit must be engineered from the front</span>.
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Revenue architecture isn't about dashboards or spreadsheets. It's about embedding commercial intelligence 
                into every pricing decision, every discount approval, every customer interaction. It's about building systems 
                that <span className="text-gold font-semibold">prevent margin leakage before it happens</span>.
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                When you architect revenue correctly, you don't chase growth—you <span className="text-gold font-semibold">govern it</span>. 
                You move from reactive firefighting to proactive margin defense. That's the difference between hoping for profit 
                and engineering it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              Investment Framework
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Structured pricing for strategic commercial transformation
            </p>
          </div>

          <div className="space-y-16 max-w-5xl mx-auto">
            {pricingTiers.map((category, catIndex) => (
              <div key={catIndex}>
                <h3 className="text-3xl font-bold text-navy mb-8 text-center">{category.category}</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {category.tiers.map((tier, tierIndex) => (
                    <Card 
                      key={tierIndex}
                      className="border-2 border-gray-200 hover:border-gold transition-all duration-300 hover:shadow-xl"
                    >
                      <CardContent className="p-8 text-center">
                        <h4 className="text-2xl font-bold text-navy mb-4">{tier.name}</h4>
                        <div className="text-4xl font-bold text-gold mb-6">{tier.price}</div>
                        <p className="text-gray-600 leading-relaxed">{tier.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-navy via-navy-light to-navy-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="h-1 w-24 bg-gold mx-auto mb-8"></div>
          
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-8">
            Ready to Engineer<br />
            <span className="text-gold">Your Revenue Growth?</span>
          </h2>
          
          <p className="text-2xl text-gray-300 mb-16 max-w-3xl mx-auto leading-relaxed">
            Let's diagnose your commercial architecture and design systems that scale profitably.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-gold hover:bg-gold-light text-navy font-bold text-xl px-16 py-8 rounded-none transition-all duration-300 hover:scale-105 shadow-2xl"
              onClick={() => window.open(calendlyLink, '_blank')}
            >
              <Calendar className="mr-3 w-7 h-7" />
              Book Strategic Review
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-gold text-gold hover:bg-gold/10 font-semibold text-xl px-16 py-8 rounded-none transition-all duration-300"
              onClick={() => window.open(whatsappLink, '_blank')}
            >
              <Phone className="mr-3 w-6 h-6" />
              {whatsappNumber}
            </Button>
          </div>
          
          <p className="text-gray-400 text-sm mt-8">
            Available for strategic consultations • Monday – Friday, 9AM – 5PM SAST
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-dark py-16 border-t border-gold/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-12">
            <div className="text-center md:text-left">
              <h3 className="text-3xl font-bold text-gold mb-3">The Profit Prophet</h3>
              <p className="text-gray-400 text-lg italic">Engineering Commercial Excellence</p>
              <p className="text-gray-500 text-sm mt-4">Honours Economics • Masters in AI</p>
            </div>
            
            <div className="text-center">
              <p className="text-gray-400 mb-3 text-sm uppercase tracking-wider">Strategic Consultations</p>
              <a 
                href={whatsappLink}
                className="text-gold hover:text-gold-light transition-colors duration-300 font-semibold text-lg block mb-2"
              >
                WhatsApp: {whatsappNumber}
              </a>
              <p className="text-gray-400">
                <a 
                  href="https://www.theprofitprophet.co.za" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gold hover:text-gold-light transition-colors duration-300"
                >
                  www.theprofitprophet.co.za
                </a>
              </p>
            </div>
          </div>
          
          <Separator className="my-8 bg-gold/20" />
          
          <div className="text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} The Profit Prophet. Revenue Architecture & Commercial Intelligence.</p>
            <div className="mt-3 flex justify-center items-center gap-4">
              <a 
                href="/privacy-policy" 
                className="text-gray-400 hover:text-gold transition-colors duration-300"
              >
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <span>T&Cs Apply</span>
            </div>
            
            <Separator className="my-6 bg-gold/10 max-w-2xl mx-auto" />
            
            <div className="mt-6 max-w-3xl mx-auto">
              <p className="text-gray-600 text-xs font-semibold mb-3 uppercase tracking-wider">Legal / Business Information</p>
              <p className="text-gray-500 text-xs leading-relaxed mb-2">
                The Profit Prophet is a pricing and profitability advisory platform founded and operated by Rohan Anson Basson, 
                based in Johannesburg, South Africa.
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">
                The platform provides AI-powered pricing assistance for contractors, service businesses, and SMEs 
                looking to structure profitable quotes.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
